import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useApp } from "../store";
function extractManualMarkers(md) {
    const out = [];
    let offset = 0;
    const lines = md.split("\n");
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        const colColIdx = line.indexOf(trimmed); // Char-Offset innerhalb der Zeile
        if (/^::pagebreak\b/.test(trimmed)) {
            const m = trimmed.match(/^::pagebreak(?:\{([^}]+)\})?/);
            const type = m?.[1] || "page";
            out.push({
                line: i + 1,
                kind: "pagebreak",
                label: `Seitenumbruch${type !== "page" ? ` (${type})` : ""}`,
                offset: offset + colColIdx,
            });
        }
        else if (/^:::keep\b/.test(trimmed)) {
            out.push({
                line: i + 1,
                kind: "keep",
                label: "Zusammenhalten",
                offset: offset + colColIdx,
            });
        }
        else if (/^:::fullpage\b/.test(trimmed)) {
            out.push({
                line: i + 1,
                kind: "fullpage",
                label: "Eigene Seite",
                offset: offset + colColIdx,
            });
        }
        offset += line.length + 1; // +1 für \n
    }
    return out;
}
export default function StructurePanel() {
    const headings = useApp((s) => s.headings);
    const images = useApp((s) => s.images);
    const toc = useApp((s) => s.settings.toc);
    const rawMarkdown = useApp((s) => s.rawMarkdown);
    const [tab, setTab] = useState("structure");
    const markers = useMemo(() => extractManualMarkers(rawMarkdown), [rawMarkdown]);
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%" }, children: [_jsxs("div", { className: "tab-bar", children: [_jsxs("button", { className: `tab ${tab === "structure" ? "active" : ""}`, onClick: () => setTab("structure"), children: ["Struktur ", headings.length > 0 && _jsx("span", { className: "tab-count", children: headings.length })] }), _jsxs("button", { className: `tab ${tab === "images" ? "active" : ""}`, onClick: () => setTab("images"), children: ["Bilder ", images.length > 0 && _jsx("span", { className: "tab-count", children: images.length })] }), _jsxs("button", { className: `tab ${tab === "markers" ? "active" : ""}`, onClick: () => setTab("markers"), children: ["Marker ", markers.length > 0 && _jsx("span", { className: "tab-count", children: markers.length })] })] }), _jsx("div", { style: { flex: 1, overflow: "auto" }, children: tab === "structure" ? (_jsx(StructureTab, { headings: headings, toc: toc })) : tab === "images" ? (_jsx(ImagesTab, { images: images, headings: headings })) : (_jsx(MarkersTab, { markers: markers })) })] }));
}
function MarkersTab({ markers }) {
    if (markers.length === 0) {
        return (_jsxs("div", { style: { padding: 14, color: "var(--ink-3)", fontSize: 13 }, children: ["Keine manuellen Marker im Dokument.", _jsx("br", {}), "Im Markdown-Tab kannst du Marker einf\u00FCgen \u2014 siehe Hilfe-Knopf in der Toolbar."] }));
    }
    return (_jsx("div", { className: "tree", children: markers.map((m, idx) => (_jsxs("div", { className: `tree-item marker-item marker-${m.kind}`, title: `Zeile ${m.line} — klicken zum Springen`, onClick: () => scrollToMarker(m.offset), style: { cursor: "pointer" }, children: [_jsx("span", { className: "num", children: m.line }), _jsx("span", { className: "name", children: m.label })] }, idx))) }));
}
/**
 * Scrollt im Preview-Iframe zum Marker-Element. Da der Pagebreak-Wrapper im
 * Layout 0px hoch ist, scrollen wir bevorzugt zu seinem Vorgänger (= dem
 * Block, vor dem der Marker steht) — sonst landen wir auf einem unsichtbaren
 * Punkt zwischen den Pages.
 */
function scrollToMarker(offset) {
    const iframe = document.querySelector("iframe.preview-iframe");
    const doc = iframe?.contentDocument;
    if (!doc)
        return;
    const el = doc.querySelector(`[data-md-start="${offset}"]`);
    if (!el)
        return;
    // Wenn der Marker selbst eine 0-Höhe-Box ist (Pagebreak), scrolle zu seinem
    // vorherigen Geschwister-Element (oder ihm selbst, wenn keins vorhanden).
    const isPagebreak = el.classList.contains("md2bp-pagebreak");
    const target = isPagebreak && el.previousElementSibling
        ? el.previousElementSibling
        : el;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
}
function StructureTab({ headings, toc }) {
    if (headings.length === 0) {
        return _jsx("div", { style: { padding: 14, color: "var(--ink-3)", fontSize: 13 }, children: "Keine \u00DCberschriften gefunden." });
    }
    return (_jsxs("div", { className: "tree", children: [_jsx("div", { className: "tree-section", children: "Frontmatter" }), _jsxs("div", { className: "tree-item", children: [_jsx("span", { className: "num", children: "\u2014" }), _jsx("span", { className: "name", children: "Titelseite" }), _jsx("span", { className: "pg", children: "i" })] }), toc.enabled && (_jsxs("div", { className: "tree-item", children: [_jsx("span", { className: "num", children: "\u2014" }), _jsx("span", { className: "name", children: toc.title }), _jsx("span", { className: "pg", children: "ii" })] })), _jsx("div", { className: "tree-section", children: "Inhalt" }), headings.map((h) => (_jsxs("div", { className: `tree-item lvl-${h.level}`, onClick: () => scrollIntoIframe(h.id), title: h.text, children: [_jsx("span", { className: "num", children: h.level === 1 ? indexOfH1(headings, h.id) : "" }), _jsx("span", { className: "name", children: h.text })] }, h.id)))] }));
}
function ImagesTab({ images, headings }) {
    if (images.length === 0) {
        return _jsx("div", { style: { padding: 14, color: "var(--ink-3)", fontSize: 13 }, children: "Keine Bilder im Dokument." });
    }
    const headingMap = new Map(headings.map((h) => [h.id, h]));
    return (_jsx("div", { style: { padding: "8px" }, children: images.map((img, i) => {
            const chapter = img.nearestHeadingId ? headingMap.get(img.nearestHeadingId) : undefined;
            return (_jsxs("div", { className: "image-item", onClick: () => scrollIntoIframeByImg(img.id), title: img.src, children: [_jsx("div", { className: "image-thumb", children: _jsx("img", { src: img.src, alt: img.alt || "", loading: "lazy" }) }), _jsxs("div", { className: "image-meta", children: [_jsxs("div", { className: "image-title", children: ["Bild ", i + 1] }), chapter && _jsx("div", { className: "image-chapter", children: chapter.text }), img.alt && _jsxs("div", { className: "image-alt", children: ["\u201E", img.alt, "\""] }), img.widthHint && _jsxs("div", { className: "image-width", children: ["w: ", img.widthHint] })] })] }, img.id));
        }) }));
}
function indexOfH1(all, id) {
    let n = 0;
    for (const h of all) {
        if (h.level === 1)
            n++;
        if (h.id === id)
            return String(n);
    }
    return "";
}
function scrollIntoIframe(id) {
    const iframe = document.querySelector("iframe.preview-iframe");
    const doc = iframe?.contentDocument;
    if (!doc)
        return;
    const target = doc.getElementById(id);
    if (target)
        target.scrollIntoView({ behavior: "smooth", block: "start" });
}
function scrollIntoIframeByImg(imgId) {
    const iframe = document.querySelector("iframe.preview-iframe");
    const doc = iframe?.contentDocument;
    if (!doc)
        return;
    const target = doc.querySelector(`[data-img-id="${imgId}"]`);
    if (target)
        target.scrollIntoView({ behavior: "smooth", block: "center" });
}
